import { ApolloError, DocumentNode, QueryHookOptions, gql } from '@apollo/client';
import { MetricType } from 'modules/campaigns/alerts/components/constants';
import { AlertStatus, ComparisonOperator, MetricName } from 'modules/campaigns/alerts/constants';
import { AllMessageMediums, TimeDuration } from 'modules/campaigns/alerts/types';
import { CampaignTypeFilter } from 'modules/domains/campaign';
import { useMemo } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';

type QueryCampaignsTriggeredAlertsVariables = {
  campaignIds: number[];
  alertTimeWindow: TimeDuration;
};
type QueryCampaignsTriggeredAlertsOptions = QueryHookOptions<
  any,
  QueryCampaignsTriggeredAlertsVariables
>;

export type CampaignAlert = {
  id: number;
  createdAt: string;
  updatedAt: string;
  nextRunAt: string;
  lastRunAt: string;
  projectId: number;
  projectCampaignAlertId: number;
  campaignId: number;
  status: AlertStatus;
  campaignType: CampaignTypeFilter;
  messageMedium: AllMessageMediums;
  metricName: MetricName;
  comparisonOperator: ComparisonOperator;
  threshold: number;
  frequency: TimeDuration;
  triggeredValue: number;
  triggeredTime: string;
  metricDisplayType: MetricType;
};

export type UseCampaignsTriggeredAlertsState = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (variables?: Partial<QueryCampaignsTriggeredAlertsOptions['variables']>) => void;

  campaignTriggeredAlerts: {
    campaignId: number;
    hasRecentAlerts: boolean;
    recentAlerts: CampaignAlert[];
    pastAlerts: CampaignAlert[];
  }[];
};

const AlertFields = `
  id
  createdAt
  updatedAt
  nextRunAt
  lastRunAt
  projectId
  projectCampaignAlertId
  campaignId
  status
  campaignType
  messageMedium
  metricName
  comparisonOperator
  threshold
  frequency
  triggeredValue
  triggeredTime
  metricDisplayType
`;

const queryCampaignsTriggeredAlerts = gql`
  query queryCampaignTriggeredAlerts($campaignIds: [Long!]!, $alertTimeWindow: String!) {
    campaignTriggeredAlerts(campaignIds: $campaignIds, alertTimeWindow: $alertTimeWindow) {
      campaignId
      hasRecentAlerts
      recentAlerts {
        ${AlertFields}
      }
      pastAlerts {
        ${AlertFields}
      }
    }
  }
`;

export const useCampaignsTriggeredAlerts = (
  options: QueryCampaignsTriggeredAlertsOptions,
  query: DocumentNode = queryCampaignsTriggeredAlerts,
): UseCampaignsTriggeredAlertsState => {
  const queryOptions: QueryCampaignsTriggeredAlertsOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      skip: !options?.variables?.campaignIds?.length || !options?.variables?.alertTimeWindow,
      ...options,
    }),
    [options],
  );

  const {
    data,
    errorMessage,
    isLoading,
    error: hasError = false,
    refetch,
  } = useGraphql(query, queryOptions);

  return {
    campaignTriggeredAlerts: data?.campaignTriggeredAlerts ?? [],
    errorMessage,
    hasError,
    isLoading,
    refetch,
  };
};
